import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import MarkdownEditor from "@/components/MarkdownEditor";
import MultiSelect from "@/components/MultiSelect";
import TagCategorySelector from "@/components/TagCategorySelector";
import { CheckCircle2, AlertCircle, Linkedin, Twitter, Facebook, Instagram, Youtube, ExternalLink, Newspaper, User, Receipt, UsersRound, Package, Trophy } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { MyCompanyBillingBody } from "./MyCompany";
import { MemberTeamBody } from "./MemberTeam";
import { MemberProductsBody } from "./MemberProducts";
import { MyScoreBody } from "./MyScore";

// Defined at MODULE LEVEL — defining these inside the parent component recreates the
// function on every render, which unmounts/remounts every input on each keystroke
// and causes the cursor to jump out of the field.
function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-wine-300"
        data-testid={testId}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-6">
      <h2 className="text-lg font-bold text-navy">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function PublicInfoBody() {
  const { t } = useLang();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | loaded | error | none
  const [ref, setRef] = useState({ poles: [], special_tags: [], activities: [], markets: [], technologies: [] });
  const [completion, setCompletion] = useState({ score: 0, missing: [] });
  const [loading, setLoading] = useState(false);
  // Sprint 92am — Projects catalog for the self-service multi-select.
  const [projectsCatalog, setProjectsCatalog] = useState([]);

  const load = async () => {
    try {
      const stats = await api.get("/my/stats");
      setC(stats.data.company);
      if (stats.data.company) {
        try {
          const comp = await api.get(`/companies/${stats.data.company.id}/completion`);
          setCompletion(comp.data);
        } catch (e) { /* completion is optional */ }
        setLoadState("loaded");
      } else {
        setLoadState("none");
      }
    } catch (e) {
      setLoadState("error");
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/profile/reference-data").then((r) => { if (!cancelled) setRef(r.data); }).catch(() => {});
    api.get("/projects").then((r) => { if (!cancelled) setProjectsCatalog(r.data || []); }).catch(() => {});
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!c) return;
    setLoading(true);
    try {
      const { id, ...payload } = c;
      await api.patch(`/companies/${id}`, payload);
      toast.success("Profil mis à jour");
      const comp = await api.get(`/companies/${id}/completion`);
      setCompletion(comp.data);
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const set = (patch) => setC({ ...c, ...patch });

  if (loadState === "loading") return <div className="py-20 text-center text-neutral-500" data-testid="company-loading">Chargement...</div>;
  if (loadState === "error") return (
    <div className="py-20 text-center" data-testid="company-error">
      <div className="mb-2 text-rose-600 font-bold">Impossible de charger votre profil entreprise</div>
      <p className="text-sm text-neutral-500">Une erreur est survenue. Veuillez recharger la page ou contacter le support.</p>
      <button onClick={() => { setLoadState("loading"); load(); }} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600">Réessayer</button>
    </div>
  );
  if (loadState === "none" || !c) return (
    <div className="py-20 text-center" data-testid="company-none">
      <div className="mb-2 text-2xl">🏢</div>
      <div className="font-bold text-navy">Aucune entreprise associée à votre compte</div>
      <p className="mt-2 text-sm text-neutral-500">{user?.role === "applicant" ? "Votre candidature est en cours d'examen. L'équipe La WineTech vous contactera prochainement." : "Contactez l'administrateur pour associer une entreprise à votre compte."}</p>
      <Link to="/portal/profile" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600">← Mon profil personnel</Link>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-navy">Informations publiques</h2>
          <p className="mt-1 text-sm text-neutral-600">{t("portal_memberprofile_01")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to={`/members/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-wine-700 hover:bg-wine-50" data-testid="view-public-profile">
              <ExternalLink className="h-3.5 w-3.5" />{t("portal_memberprofile_02")}</Link>
            <Link to="/portal/news" className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid="publish-news-cta">
              <Newspaper className="h-3.5 w-3.5" />{t("portal_memberprofile_03")}</Link>
          </div>
        </div>
        <div className="min-w-[220px] rounded-2xl border border-wine-100 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-wine-700">{t("portal_memberprofile_04")}</div>
            <div className="text-2xl font-bold text-wine-600" data-testid="completion-score">{completion.score}%</div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-wine-50">
            <div className="h-full rounded-full bg-gradient-to-r from-wine-400 to-wine-600 transition-all" style={{ width: `${completion.score}%` }} />
          </div>
          {completion.missing?.length > 0 ? (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-700"><AlertCircle className="h-3 w-3" /> {completion.missing.length} champ{completion.missing.length > 1 ? "s" : ""} à compléter</div>
          ) : (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Profil complet !</div>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Section title={t("portal_memberprofile_90")}>
          <Field label="Nom" value={c.name} onChange={(v) => set({ name: v })} testId="profile-name" />
          <ImageUpload value={c.logo} onChange={(url) => set({ logo: url })} label="Logo" testId="profile-logo" />
          <ImageUpload value={c.banner} onChange={(url) => set({ banner: url })} label={t("portal_memberprofile_91")} testId="profile-banner" />
          <Field label="Site web" value={c.website} onChange={(v) => set({ website: v })} testId="profile-website" />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("portal_memberprofile_92")} type="number" value={c.founded_year} onChange={(v) => set({ founded_year: parseInt(v) || null })} testId="profile-founded" />
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Taille</label>
              <select value={c.employees_count || ""} onChange={(e) => set({ employees_count: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm" data-testid="profile-size">
                <option value="">—</option>
                <option>1-10</option><option>11-50</option><option>51-200</option><option>201-500</option><option>500+</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville" value={c.city} onChange={(v) => set({ city: v })} testId="profile-city" />
            <Field label="Pays" value={c.country} onChange={(v) => set({ country: v })} testId="profile-country" />
          </div>
        </Section>

        <Section title={t("portal_memberprofile_93")}>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Description courte</label>
            <textarea rows={3} value={c.description || ""} onChange={(e) => set({ description: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="profile-description" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("portal_memberprofile_12")}</label>
            <div className="mt-1"><MarkdownEditor value={c.long_description || ""} onChange={(v) => set({ long_description: v })} rows={8} testId="profile-long-description" /></div>
          </div>
        </Section>

        <Section title="Classification">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("portal_memberprofile_13")}</label>
            <select value={c.main_pole || ""} onChange={(e) => set({ main_pole: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm" data-testid="profile-pole">
              <option value="">—</option>
              {ref.poles.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("portal_memberprofile_14")}</label>
            <div className="mt-2"><MultiSelect options={ref.special_tags} value={c.special_tags || []} onChange={(v) => set({ special_tags: v })} testId="profile-special-tags" /></div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("portal_memberprofile_15")}</label>
            <div className="mt-2"><MultiSelect options={ref.activities} value={c.secondary_activities || []} onChange={(v) => set({ secondary_activities: v })} testId="profile-activities" /></div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{t("portal_memberprofile_16")}</label>
            <div className="mt-2"><MultiSelect options={ref.markets} value={c.markets || []} onChange={(v) => set({ markets: v })} testId="profile-markets" /></div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Technologies</label>
            <div className="mt-2"><MultiSelect options={ref.technologies} value={c.technologies || []} onChange={(v) => set({ technologies: v })} testId="profile-techs" /></div>
          </div>
        </Section>

        <Section title={t("portal_memberprofile_94")}>
          <div className="grid grid-cols-1 gap-3">
            {[
              { k: "linkedin", icon: Linkedin, ph: "https://linkedin.com/company/..." },
              { k: "twitter", icon: Twitter, ph: "https://x.com/..." },
              { k: "facebook", icon: Facebook, ph: "https://facebook.com/..." },
              { k: "instagram", icon: Instagram, ph: "https://instagram.com/..." },
              { k: "youtube", icon: Youtube, ph: "https://youtube.com/@..." },
            ].map((s) => (
              <div key={s.k} className="flex items-center gap-2">
                <s.icon className="h-4 w-4 text-neutral-500" />
                <input value={c[s.k] || ""} onChange={(e) => set({ [s.k]: e.target.value })} placeholder={s.ph} className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid={`profile-${s.k}`} />
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="mt-6 rounded-2xl border border-neutral-100 bg-white p-6" data-testid="profile-tags-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-bold text-navy">Tags d&apos;exploration</h3>
            <p className="mt-0.5 text-xs text-neutral-500">{t("portal_memberprofile_17")}</p>
          </div>
        </div>
        <div className="mt-5">
          <TagCategorySelector
            values={c}
            onChange={(k, next) => set({ [k]: next })}
            testId="profile-tags"
          />
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-pink-100 bg-pink-50/30 p-6" data-testid="profile-projects-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-bold text-navy inline-flex items-center gap-2">🚀 Projets WineTech</h3>
            <p className="mt-0.5 text-xs text-neutral-600">Sélectionnez les projets collectifs auxquels votre entreprise contribue. Vous apparaîtrez sur la page publique de chaque projet.</p>
          </div>
        </div>
        <div className="mt-5">
          <select
            multiple
            value={c.project_ids || []}
            onChange={(e) => set({ project_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
            size={Math.min(Math.max(projectsCatalog.length, 3), 6)}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
            data-testid="profile-projects-select"
          >
            {projectsCatalog.length === 0 && <option disabled>— Aucun projet WineTech actif —</option>}
            {projectsCatalog.map((p) => (
              <option key={p.id} value={p.id}>{p.title}{p.subtitle ? ` — ${p.subtitle}` : ""}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-neutral-500">Ctrl/Cmd + clic pour multi-sélection.</p>
          {(c.project_ids || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.project_ids.map((pid) => {
                const p = projectsCatalog.find((x) => x.id === pid);
                return p ? (
                  <a key={pid} href={`/projects/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-3 py-1 text-xs font-semibold text-pink-700 hover:bg-pink-200" data-testid={`profile-project-${pid}`}>
                    🚀 {p.title}
                  </a>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button onClick={save} disabled={loading} className="rounded-full bg-wine-500 px-7 py-3 text-sm font-semibold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="profile-save">
          {loading ? "Enregistrement..." : "Enregistrer mon profil"}
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={c.public || false} onChange={(e) => set({ public: e.target.checked })} className="accent-wine-500" data-testid="profile-public" />
          Profil public visible
        </label>
      </div>
    </div>
  );
}

const TABS = [
  { key: "public", label: "Informations publiques", icon: User },
  { key: "billing", label: "Facturation & adhésion", icon: Receipt },
  { key: "team", label: "Équipe", icon: UsersRound },
  { key: "products", label: "Produits & services", icon: Package },
  { key: "score", label: "Score", icon: Trophy },
];

export default function MemberProfile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = TABS.find((t) => t.key === searchParams.get("tab"))?.key || "public";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "public") next.delete("tab"); else next.set("tab", tab);
      return next;
    }, { replace: true });
  }, [tab, setSearchParams]);

  return (
    <PortalLayout sidebar="member">
      <div>
        <h1 className="text-3xl font-bold text-navy md:text-4xl">Profil entreprise</h1>
        <p className="mt-1 text-neutral-600">Gérez vos informations publiques, facturation, équipe, produits et suivez votre score.</p>
      </div>

      <div className="mt-6 -mx-1 overflow-x-auto" data-testid="profile-tabs">
        <div className="inline-flex gap-1 rounded-full bg-neutral-100 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition ${active ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600 hover:text-wine-700"}`}
                data-testid={`profile-tab-${t.key}`}
              >
                <Icon className="h-3.5 w-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6" data-testid={`profile-tab-content-${tab}`}>
        {tab === "public" && <PublicInfoBody />}
        {tab === "billing" && <MyCompanyBillingBody />}
        {tab === "team" && <MemberTeamBody />}
        {tab === "products" && <MemberProductsBody />}
        {tab === "score" && <MyScoreBody />}
      </div>
    </PortalLayout>
  );
}
